/**
 * 한국 시간(Asia/Seoul) 고정 날짜 유틸
 *
 * 장례 일정(별세·입관·발인 등)은 한국 달력 기준의 고정된 날짜다. 해외(다른 타임존)에서
 * 부고장을 열거나 문자·카카오로 공유해도 항상 한국 기준 연·월·일·시·분이 보여야 하므로,
 * 브라우저 로컬 getter(getFullYear/getHours…) 대신 이 유틸을 쓴다.
 *
 * - 읽기: toKstParts(instant) → KST 달력 구성요소
 * - 쓰기: fromKstParts(y, m, d, h, mi) → 순간값(Date)
 *
 * KST는 서머타임이 없어 고정 오프셋(+9h)으로 충분하다. 한국에서 쓰는 사용자에게는
 * 로컬 = KST 이므로 결과가 기존과 동일하다.
 */

export const KST_TIME_ZONE = 'Asia/Seoul';
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface KstParts {
  year: number;
  /** 1~12 */
  month: number;
  date: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** 0=일 … 6=토 */
  day: number;
}

/**
 * 순간값 → KST 달력 구성요소.
 * Intl 없이 오프셋 산술로 계산한다(DST 없음). UTC getter는 타임존 무관이라 어디서 열어도 동일.
 */
export const toKstParts = (input: Date | string | number): KstParts => {
  const shifted = new Date(new Date(input).getTime() + KST_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    date: shifted.getUTCDate(),
    hours: shifted.getUTCHours(),
    minutes: shifted.getUTCMinutes(),
    seconds: shifted.getUTCSeconds(),
    day: shifted.getUTCDay(),
  };
};

/** KST 달력값(연, 월 1~12, 일, 시, 분) → 순간값 */
export const fromKstParts = (
  year: number,
  month: number,
  date: number,
  hours = 0,
  minutes = 0,
  seconds = 0,
): Date => new Date(Date.UTC(year, month - 1, date, hours, minutes, seconds) - KST_OFFSET_MS);

/** 해당 순간이 속한 KST 날짜의 자정(00:00 KST) 순간값 */
export const kstStartOfDay = (input: Date | string | number): Date => {
  const p = toKstParts(input);
  return fromKstParts(p.year, p.month, p.date);
};

/** 순간값에 KST 기준 n일을 더한다(오프셋 고정이므로 24h 배수) */
export const addKstDays = (input: Date | string | number, days: number): Date =>
  new Date(new Date(input).getTime() + days * DAY_MS);

/**
 * 순간값의 KST 날짜(연·월·일)를 브라우저 로컬 달력 Date로 옮긴다.
 * 달력 UI(로컬 Date 산술로 그리드를 만드는 DatePickerModal)에 초기 선택일을 넘길 때 사용.
 * 반대 방향은 calendarDateToKst.
 */
export const kstToCalendarDate = (input: Date | string | number): Date => {
  const p = toKstParts(input);
  return new Date(p.year, p.month - 1, p.date);
};

/** 달력 UI가 고른 로컬 Date의 연·월·일을 KST 자정 순간값으로 해석한다 */
export const calendarDateToKst = (calendarDate: Date): Date =>
  fromKstParts(
    calendarDate.getFullYear(),
    calendarDate.getMonth() + 1,
    calendarDate.getDate(),
  );

/** 두 순간값이 KST 기준 같은 날짜인지 */
export const isSameKstDate = (a: Date | string | number, b: Date | string | number) => {
  const pa = toKstParts(a);
  const pb = toKstParts(b);
  return pa.year === pb.year && pa.month === pb.month && pa.date === pb.date;
};
